import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'reactstrap'
import { FormProvider, useForm } from 'react-hook-form'
import { SpinnerContext } from '../../context/spinnerContext'
import { getAllRegion, updateRegionDetails } from '../../redux/slices/regionSlice'
import { getAdminByRegion, getAllAdmin } from '../../redux/slices/adminControlSlice'
import { getCityByRegion } from '../../redux/slices/citySlice'
import CardView from './cardView'
import CustomModal from '../../component/customModal'
import NewRegionAdmin from './addEditForms/newRegionAdmin'
import NewRegion from './addEditForms/newRegion'
import NewCity from './addEditForms/newCity'
import NewAdmin from './addEditForms/newAdmin'
import { errorToast, successToast } from '../../util/Toast/toast'
import { getUserRole, hasAccess } from '../../helper/function'

const Region = () => {
  const methods = useForm()
  const { setValue, watch } = methods
  const dispatch = useDispatch()
  const { changeLoader } = useContext(SpinnerContext)

  const { allRegion, getDetailsByRegion } = useSelector(({ region }) => region)
  const { allCityByRegion } = useSelector(({ city }) => city)
  const { allAdminByRegion } = useSelector(({ adminControl }) => adminControl)

  const [regionID, setRegionID] = useState(null)
  const [isRefresh, setIsRefresh] = useState(false)

  const [loading, setLoading] = useState({
    city: false,
    regionAdmin: false
  })

  const [modals, setModals] = useState({
    newRegionAdmin: false,
    newRegion: false,
    newCity: false,
    newAdmin: false
  })

  const toggleModal = (modalType) => {
    setModals((prevState) => ({
      ...prevState,
      [modalType]: !prevState[modalType]
    }))
  }

  const handleApiCall = async (apiAction, setLoadingType) => {
    setLoading((prevState) => ({ ...prevState, [setLoadingType]: true }))
    try {
      await dispatch(apiAction())
    } catch (err) {
      console.log('Error :>>', err)
    } finally {
      setLoading((prevState) => ({ ...prevState, [setLoadingType]: false }))
    }
  }

  useEffect(() => {
    changeLoader(true)

    const apiCalls = [dispatch(getAllRegion())]

    // Check the user's role before adding getAllAdmin to the API calls
    if (hasAccess('newRegionalAdmin', getUserRole())) {
      apiCalls.push(dispatch(getAllAdmin()))
    }

    Promise.all(apiCalls)
      .finally(() => changeLoader(false))
      .catch((err) => console.log('Error :>>', err))
  }, [isRefresh])

  useEffect(() => {
    if (regionID) {
      handleApiCall(() => getCityByRegion(regionID), 'city')
      hasAccess('newRegionalAdmin', getUserRole()) && handleApiCall(() => getAdminByRegion(regionID), 'regionAdmin')
    }
  }, [regionID, isRefresh])

  useEffect(() => {
    setValue('regionDelete', [
      {
        ...getDetailsByRegion,
        admins: getDetailsByRegion?.admins?.map((item) => ({ ...item, region_id: getDetailsByRegion?.region })),
        cities: getDetailsByRegion?.cities?.map((item) => ({ ...item, region_id: getDetailsByRegion?.region }))
      }
    ])
  }, [getDetailsByRegion])

  const onHandleSubmit = () => {
    const modifiedItems = watch('regionDelete')
    const updatedValue = {
      adminRegions: [],
      cities: []
    }

    if (modifiedItems[0]?.admins) {
      updatedValue.adminRegions = modifiedItems[0].admins
        .map((item) =>
          typeof item?.region_id !== 'string' ? { admin_region_id: item?.admin_region_id, region_id: item?.region_id?.region_id } : null
        )
        ?.filter((item) => item?.region_id !== getDetailsByRegion?.region?.region_id)
    }

    if (modifiedItems[0]?.cities) {
      updatedValue.cities = modifiedItems[0].cities
        .map((item) => (typeof item?.region_id !== 'string' ? { city_id: item?.city_id, region_id: item?.region_id?.region_id } : null))
        .filter((item) => item?.region_id !== getDetailsByRegion?.region?.region_id)
    }
    updateRegionDetailsById(updatedValue)
  }
  const updateRegionDetailsById = (data) => {
    const modifiedItems = watch('regionDelete')
    const payload = {
      region_id: modifiedItems[0]?.region?.region_id,
      data: data
    }
    changeLoader(true)
    dispatch(updateRegionDetails(payload))
      .then(({ payload, error }) => {
        if (payload?.status === 200) {
          successToast(payload?.data?.data?.message || payload?.data?.status_message || 'Deleted successfully!')
          setIsRefresh(true)
        } else {
          errorToast(payload?.data?.status_message || error?.message)
        }
        changeLoader(false)
      })
      .catch(() => {
        changeLoader(false)
      })
  }

  return (
    <>
      <div className="page-wrapper customers">
        <div className="content">
          <div className="page-header">
            <div className="content-page-header">
              <h5>Regions</h5>

              {hasAccess('newRegionButton', getUserRole()) && (
                <div className="list-btn">
                  <ul className="filter-list">
                    <li>
                      <Button className="btn btn-primary" onClick={() => toggleModal('newRegion')}>
                        <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                        New Region
                      </Button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
          <FormProvider {...methods}>
            {/* Regions */}
            <CardView listData={allRegion} keyName="region" setRegionID={setRegionID} setIsRefresh={setIsRefresh} onHandleSubmit={onHandleSubmit} />
          </FormProvider>

          {/* Cities */}
          <div className="common_heading_div">
            <h4>Cities</h4>
            <Button className="btn btn-primary" onClick={() => toggleModal('newCity')}>
              <i className="fa fa-plus-circle me-2" aria-hidden="true" />
              New City
            </Button>
          </div>
          <CardView listData={allCityByRegion} keyName="city" setIsRefresh={setIsRefresh} isLoading={loading.city} />

          {/* Regional Admin */}
          {hasAccess('newRegionalAdmin', getUserRole()) && (
            <>
              <div className="common_heading_div">
                <h4>Regional Admin</h4>
                <Button className="btn btn-primary" onClick={() => toggleModal('newRegionAdmin')}>
                  <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                  Add Region Admin
                </Button>
              </div>
              <CardView
                listData={allAdminByRegion}
                keyName="first_name"
                keyName2="last_name"
                keyName3="email"
                setIsRefresh={setIsRefresh}
                isLoading={loading.regionAdmin}
              />
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {modals.newRegionAdmin && (
        <CustomModal isOpen={modals.newRegionAdmin} toggle={() => toggleModal('newRegionAdmin')} title="New Regional Admin" size="md">
          <NewRegionAdmin toggle={() => toggleModal('newRegionAdmin')} setIsRefresh={setIsRefresh} regionID={regionID} />
        </CustomModal>
      )}

      {modals.newRegion && (
        <CustomModal isOpen={modals.newRegion} toggle={() => toggleModal('newRegion')} title="New Region" size="md">
          <NewRegion toggle={() => toggleModal('newRegion')} setIsRefresh={setIsRefresh} />
        </CustomModal>
      )}

      {modals.newCity && (
        <CustomModal isOpen={modals.newCity} toggle={() => toggleModal('newCity')} title="New City" size="md">
          <NewCity toggle={() => toggleModal('newCity')} setIsRefresh={setIsRefresh} regionID={regionID} />
        </CustomModal>
      )}

      {modals.newAdmin && (
        <CustomModal isOpen={modals.newAdmin} toggle={() => toggleModal('newAdmin')} title="New Admin" size="md">
          <NewAdmin toggle={() => toggleModal('newAdmin')} setIsRefresh={setIsRefresh} />
        </CustomModal>
      )}
    </>
  )
}

export default Region
